import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const CancellationPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <img src="/kyle-drive-logo.png" alt="Kyle Drive" className="h-20 w-auto" />
            <div>
              <h1 className="text-4xl font-bold text-[hsl(210,100%,20%)]">Cancellation & Refund Policy</h1>
              <p className="text-gray-600 mt-2">Effective December 2025</p>
            </div>
          </div>

          <div className="space-y-8 text-gray-700">
            <section>
              <p className="leading-relaxed text-lg">
                At Kyle Drive, we understand that plans can change. Our cancellation policy is designed to be fair and proportionate based on the notice you provide.
              </p>
            </section>

            <section className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-2xl p-6 border-2 border-cyan-200">
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-6 flex items-center gap-3">
                <Clock className="w-7 h-7 text-cyan-600" />
                Refund Structure Based on Notice Period
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md">
                  <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">7+ Days Notice</h3>
                    <p className="text-gray-700 font-semibold text-lg mb-1">100% Full Refund</p>
                    <p className="text-gray-600">Cancel 7 or more days before your session for a complete refund. No questions asked.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md">
                  <CheckCircle className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">3-6 Days Notice</h3>
                    <p className="text-gray-700 font-semibold text-lg mb-1">75% Refund</p>
                    <p className="text-gray-600">Cancel 3-6 days before your session. We'll refund 75% of your booking cost.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md">
                  <AlertCircle className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">48-72 Hours Notice</h3>
                    <p className="text-gray-700 font-semibold text-lg mb-1">50% Refund</p>
                    <p className="text-gray-600">Cancel 2-3 days before your session. We'll refund 50% of your booking cost.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md">
                  <AlertCircle className="w-8 h-8 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">24-48 Hours Notice</h3>
                    <p className="text-gray-700 font-semibold text-lg mb-1">25% Refund</p>
                    <p className="text-gray-600">Cancel 1-2 days before your session. We'll refund 25% of your booking cost, or you can reschedule once for free.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md border-2 border-red-200">
                  <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Less Than 24 Hours / No-Show</h3>
                    <p className="text-gray-700 font-semibold text-lg mb-1">No Refund</p>
                    <p className="text-gray-600">Cancellations within 24 hours or no-shows are non-refundable. We may offer one-time rescheduling at our discretion.</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">Rescheduling Options</h2>
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <p className="leading-relaxed mb-3">
                  <strong>Free Rescheduling:</strong> You can reschedule your session once for free if you provide at least 48 hours notice.
                </p>
                <p className="leading-relaxed">
                  <strong>Additional Rescheduling:</strong> Second and subsequent reschedules may incur a \u00a35 administrative fee.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">Special Circumstances</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Medical Emergencies</h3>
                  <p className="leading-relaxed">
                    If you need to cancel due to sudden illness or medical emergency, please contact us. We'll work with you on a case-by-case basis and may offer full rescheduling or refund with appropriate documentation.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Extreme Weather (Multi-Storey Protected)</h3>
                  <p className="leading-relaxed">
                    As Kyle Rise is a covered multi-storey car park, weather rarely affects sessions. In exceptional circumstances where access is unsafe, we'll offer full refunds or free rescheduling.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Kyle Drive Cancellations</h3>
                  <p className="leading-relaxed">
                    If we need to cancel your session (instructor illness, facility issues), you'll receive a full refund or priority rebooking with no penalties.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">Group & School Bookings</h2>
              <p className="leading-relaxed">
                Group bookings (5+ participants) have more flexible cancellation terms. Please contact us directly to discuss your specific requirements. Deposits may be required for large group bookings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">How to Cancel or Reschedule</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">To cancel or reschedule your booking:</p>
                <ol className="list-decimal list-inside space-y-2 pl-4">
                  <li>Email us at <a href="mailto:wheels@kyledrive.co.uk" className="text-cyan-600 hover:text-cyan-700 font-semibold">wheels@kyledrive.co.uk</a> with your booking reference</li>
                  <li>Include your name, session date/time, and reason for cancellation (optional)</li>
                  <li>We'll confirm your cancellation and refund details within 24 hours</li>
                  <li>Refunds are processed within 5-7 business days to your original payment method</li>
                </ol>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">Contact Us</h2>
              <div className="bg-cyan-50 rounded-xl p-6 border-2 border-cyan-200">
                <p className="font-semibold text-gray-900">Kyle Drive</p>
                <p className="text-gray-700">Kyle Rise Car Park, Carrick Street, Ayr, KA7 1RZ</p>
                <p className="text-gray-700 mt-2">
                  <strong>Email:</strong> <a href="mailto:wheels@kyledrive.co.uk" className="text-cyan-600 hover:text-cyan-700">wheels@kyledrive.co.uk</a>
                </p>
                <p className="text-gray-600 text-sm mt-4">
                  We're here to help! If you have questions about our cancellation policy or need assistance, don't hesitate to reach out.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;
